import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import SegmentsPanel from './SegmentsPanel';
import OptionsPanel from './OptionsPanel';
import HumanReviewBanner from '../common/HumanReviewBanner';
import ProgressPage from '../results/ProgressPage';
import ResultsPage from '../results/ResultsPage';
import LoadingSpinner from '../common/LoadingSpinner';
import { 
  createEvaluation, 
  updateEvaluation, 
  getEvaluation, 
  evaluate, 
  estimateEvaluation, 
  duplicateEvaluation, 
  listModels,
  getPrompt,
  getConfig
} from '../../services/evaluationService';
import { addSegments, getSegments, deleteSegments, updateSegments } from '../../services/segmentService';
import ErrorDialog from '../common/ErrorDialog';
import { sendGA4Event } from "../../utils/googleAnalyticsUtils";
import NotFound from '../layout/NotFound';
import { isValidID } from '../../utils/idGenerator';
import { evaluateAdminEvaluation } from '../../services/adminService';
//import { languages } from '../../utils/languages';
import { detectLanguageFromText } from '../../services/languageService';
import useDebouncedCallback from '../../hooks/useDebouncedCallback';

const UNTITLED_EVALUATION = "Untitled Evaluation";
const EDITOR_PAGE = "editor";
const PROGRESS_PAGE = "progress";
const RESULTS_PAGE = "results";

async function pollEvaluationStatus(evaluationId, onUpdate, delay = 5000) {
  while (true) {
    const evaluation = await getEvaluation(evaluationId);
    if (onUpdate && evaluation.statistics?.queued !== undefined) {
      onUpdate(evaluation.statistics.queued);
    }
    if (evaluation.status !== 3) {
      return evaluation;
    }
    await new Promise(resolve => setTimeout(resolve, delay));
  }
}

const EvaluatePage = () => {
  const { id: evaluationId } = useParams();
  const { headerRef } = useOutletContext();
  const [segments, setSegments] = useState([
    { id: 1, serverId: null, source: '', target: '' },
    { id: 2, serverId: null, source: '', target: '' },
    { id: 3, serverId: null, source: '', target: '' },
  ]);

  const [sourceLanguage, setSourceLanguage] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [isSourceAutoDetected, setIsSourceAutoDetected] = useState(false);
  const [isTargetAutoDetected, setIsTargetAutoDetected] = useState(false);
  const [currentView, setCurrentView] = useState(EDITOR_PAGE); // 'editor', 'progress', 'results'
  const [isLoading, setIsLoading] = useState(true);
  const [evaluationData, setEvaluationData] = useState(null);
  const [estimates, setEstimates] = useState(null);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [fullListModels, setFullListModels] = useState([]);
  const [error, setError] = useState({ isOpen: false, message: '', progressDisplay: false });
  const [promptText, setPromptText] = useState('');
  const [processedSegments, setProcessedSegments] = useState(0);
  const [limits, setLimits] = useState({ maxSegments: 10, maxChars: 1000 });
  const [guidelines, setGuidelines] = useState('');
  const segmentsRef = useRef(segments);

  useEffect(() => {
    const isMountedRef = { current: true };
  
    const fetchAndSetModels = async () => {
      const models = await listModels();
      setFullListModels(models);
      console.log('models', models);
  
      if (models.length > 0) {
        const initialModelId = models[0].id;
        setSelectedModelId(initialModelId);
      }
      return models;
    };
  
    const setHeaderName = (name) => {
      headerRef?.current?.setEvaluationName?.(name || UNTITLED_EVALUATION);
    };
  
    const handleEvaluation = async (models, evalId) => {
      const originalId = localStorage.getItem("original_id");
      const duplicateKey = `duplicate_${originalId}`;
      const isDuplicate = localStorage.getItem(duplicateKey) === "true";
      const currentEvalId = isDuplicate && originalId ? originalId : evalId;
  
      let evaluation = await getEvaluation(currentEvalId);
      if (!isMountedRef.current) return;
  
      let isNewEvaluation = false;
      if (!evaluation) {
        await createEvaluation({
          id: evalId,
          name: UNTITLED_EVALUATION
        });
        setHeaderName(UNTITLED_EVALUATION);
        evaluation = await getEvaluation(evalId); // refetch
        isNewEvaluation = true;
      }
  
      if (!evaluation) return; 
  
      setSourceLanguage(evaluation.sourceLanguage || "");
      setTargetLanguage(evaluation.targetLanguage || "");
      setGuidelines(evaluation.guidelines || "");
  
      if (isDuplicate) {
        localStorage.removeItem(duplicateKey);
        localStorage.removeItem("original_id");
      }
  
      const evalModel = evaluation.model?.trim();
      const exists = models.some(m => m.id === evalModel);
      const modelId = exists ? evalModel : models?.[0]?.id ?? '';
  
      const duplicateResponse = isDuplicate
        ? await duplicateEvaluation(originalId, evalId, modelId, evaluation.name)
        : null;
  
      //or modelId ???
      await updateEvaluation(evalId, "modelId", modelId);
      setSelectedModelId(modelId);

      const segmentsResponse = await getSegments(evalId);
      // !!! call estimateEvaluation for new evaluations, not when loading existing ones
      const estimateResponse = (isNewEvaluation && !isDuplicate)
        ? await estimateEvaluation(currentEvalId, modelId)
        : null;

      const updatedEvaluation = {
        ...(duplicateResponse || evaluation),
        segments: segmentsResponse.segments,
      };
      setEvaluationData(updatedEvaluation);
  
      const displayName = isDuplicate
        ? `${duplicateResponse.evaluation.name} Copy`
        : evaluation.name;
      setHeaderName(displayName);
  
      if ([3, 4, 6].includes(evaluation.status) && !isDuplicate) {
        setCurrentView(RESULTS_PAGE);
      } else {
        if (models.length > 0 && modelId) {
          getPrompt(modelId)
            .then((prompt) => {
              if (isMountedRef.current) {
                const text = typeof prompt === 'string' ? prompt : prompt?.evaluationPrompt ?? '';
                setPromptText(text);
              }
            })
            .catch((err) => console.error('Error fetching prompt:', err));
        }

        if (segmentsResponse?.segments?.length) {
          const formatted = segmentsResponse.segments.map((segment, i) => ({
            id: i + 1,
            source: segment.sourceText || "",
            target: segment.targetText || "",
            serverId: segment.id,
          }));
          setSegments(formatted);
          setEstimates(estimateResponse || duplicateResponse?.estimate);
        }
        setCurrentView(EDITOR_PAGE);
      }
    };
  
    const loadInitialData = async () => {
      try {
        const models = await fetchAndSetModels();
        //if (!models) throw new Error("No models loaded");
        await handleEvaluation(models, evaluationId);
      } catch (error) {
        console.error("Failed to load initial data:", error);
        setError({
          isOpen: true,
          message: "Failed to load initial data. Please try again.",
        });
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }

      const config = await getConfig();
      setLimits({ maxSegments: config.maxSegments || 10, maxChars: config.maxChars || 1000 });
    };
  
    loadInitialData();
  
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps    
  }, [evaluationId]);
  
  const handleAddSegment = () => {
    const newId = segments.length > 0 ? Math.max(...segments.map(s => s.id)) + 1 : 1;
    setSegments([...segments, { id: newId, serverId: null, source: '', target: '' }]);
  };

  const handleRemoveSegment = async (segmentId) => {
    const segmentToRemove = segments.find(segment => segment.id === segmentId);
    const updatedSegments = segments.filter(segment => segment.id !== segmentId);
    setSegments(updatedSegments);

    try {
      if (segmentToRemove?.serverId) {
        await deleteSegments(evaluationId, [segmentToRemove.serverId]);
        
        if (selectedModelId) {
          const estimateResponse = await estimateEvaluation(evaluationId, selectedModelId);
          setEstimates(estimateResponse);
        }
      }

      const hasAnySourceContent = updatedSegments.some(segment => segment.source.trim());
      const hasAnyTargetContent = updatedSegments.some(segment => segment.target.trim());

      if (!hasAnySourceContent) {
        setSourceLanguage('');
        setIsSourceAutoDetected(false);
      }

      if (!hasAnyTargetContent) {
        setTargetLanguage('');
        setIsTargetAutoDetected(false);
      }
    } catch (error) {
      console.error('Failed to remove segment:', error);
      setError({
        isOpen: true,
        message: 'Failed to remove segment. Please try again.'
      });
    }
  };

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  // const debouncedAutoDetect = useDebouncedCallback(
  //   async (segmentId, field, value) => {
  //     const isAuto = field === 'source' ? isSourceAutoDetected : isTargetAutoDetected;
  //     const currentLang = field === 'source' ? sourceLanguage : targetLanguage;
  //     const setLang = field === 'source' ? handleSourceLanguageChange : handleTargetLanguageChange;
  //     const setAuto = field === 'source' ? setIsSourceAutoDetected : setIsTargetAutoDetected;
  
  //     if (!isAuto || value.trim().length < 1) return;
  
  //     try {
  //       const matched = await detectLanguageFromText(value.trim());
  //       if (matched?.name && matched.name !== currentLang) {
  //         setLang(matched.name);
  //         setAuto(true);
  //       }
  //     } catch (err) {
  //       console.error('Autodetect failed:', err);
  //     }
  //   }, 300 //delay 300ms
  // );
  
  const debouncedSaveSegment = useDebouncedCallback(
    async (segmentId) => {
      const segment = segmentsRef.current.find(s => s.id === segmentId);
      if (!segment) return;
  
      try {
        if (segment.source.trim() || segment.target.trim()) {
          if (segment.serverId) {
            await updateSegments({
              evaluationId,
              segments: [{
                id: segment.serverId,
                sourceText: segment.source,
                targetText: segment.target
              }]
            });
          } else {
            const response = await addSegments(evaluationId, [{
              source: segment.source,
              target: segment.target
            }]);
  
            if (response?.[0]?.id) {
              setSegments(prev =>
                prev.map(s =>
                  s.id === segmentId ? { ...s, serverId: response[0].id } : s
                )
              );
            }
          }
  
          if (selectedModelId) {
            const estimate = await estimateEvaluation(evaluationId, selectedModelId);
            setEstimates(estimate);
          }
        }
      } catch (err) {
        console.error('Save segment failed:', err);
        setError({
          isOpen: true,
          message: 'Failed to update segment. Please try again.'
        });
      }
    }, 700 //delay 700ms
  );
  
  const handleSegmentChange = (segmentId, field, value) => {
    setSegments(prev => prev.map(segment =>
      segment.id === segmentId ? { ...segment, [field]: value } : segment
    ));
  
    // if (segmentId === 1) { debouncedAutoDetect(segmentId, field, value); }
    debouncedSaveSegment(segmentId);
  };

  const handlePaste = async (segmentId, field, event) => {
    // const pastedText = event.clipboardData.getData('text');
    // if (pastedText.trim().length >= 1 && segmentId === 1) {
    //   try {
    //     const matchedLang = await detectLanguageFromText(pastedText.trim());
    //     if (matchedLang) {
    //       if (field === 'source' && isSourceAutoDetected) {
    //         handleSourceLanguageChange(matchedLang.name);
    //         setIsSourceAutoDetected(true);
    //       } else if (field === 'target' && isTargetAutoDetected) {
    //         handleTargetLanguageChange(matchedLang.name);
    //         setIsTargetAutoDetected(true);
    //       }
    //     }
    //   } catch (error) {
    //     console.error('Language detection failed on paste:', error);
    //   }
    // }
  };

  const handleFileUpload = async (newSegments, segmentIdsToDelete = []) => {
    if (segmentIdsToDelete.length > 0) {
      try {
        await deleteSegments(evaluationId, segmentIdsToDelete);
        
        if (selectedModelId) {
          const estimateResponse = await estimateEvaluation(evaluationId, selectedModelId);
          setEstimates(estimateResponse);
        }
      } catch (error) {
        console.error('Failed to delete segments:', error);
        setError({
          isOpen: true,
          message: 'Failed to delete segments. Please try again.'
        });
      }
    }

    if (newSegments && newSegments.length > 0) {
      setSegments(newSegments);

      try {
        const addSegmentsResponse = await addSegments(evaluationId, newSegments.map(segment => ({
          source: segment.source,
          target: segment.target
        })));

        if (addSegmentsResponse && addSegmentsResponse.length > 0) {
          const updatedSegments = newSegments.map((segment, index) => ({
            ...segment,
            serverId: addSegmentsResponse[index]?.id || null
          }));
          setSegments(updatedSegments);
        }

        if (selectedModelId) {
          const estimateResponse = await estimateEvaluation(evaluationId, selectedModelId);
          setEstimates(estimateResponse);
        }
      } catch (error) {
        console.error('Failed to process uploaded segments:', error);
        setError({
          isOpen: true,
          message: 'Failed to process uploaded segments. Please try again.'
        });
      }
    } else {
      //TODO: develop this feature
      // const segmentsResponse = await getSegments(evaluationId);
      // console.log('segmentsResponse', segmentsResponse);
      setSegments([{ id: 1, serverId: null, source: '', target: '' }]);
    }
  };

  const handleEvaluate = async (engine) => {
    try {      
      const nonEmptySegments = segments.filter(segment => 
        segment.source.trim() !== '' || segment.target.trim() !== ''
      );

      if (nonEmptySegments.length === 0) {
        return;
      }

      setCurrentView(PROGRESS_PAGE);

      await updateEvaluation(evaluationId, 'sourceLanguage', sourceLanguage);
      await updateEvaluation(evaluationId, 'targetLanguage', targetLanguage);
      await updateEvaluation(evaluationId, 'guidelines', guidelines);

      const newSegments = nonEmptySegments.filter(segment => !segment.serverId);
      if (newSegments.length > 0) {
        await addSegments(evaluationId, newSegments);
      }

      const admin = !!localStorage.getItem('token');
      if (admin) {
        await evaluateAdminEvaluation(evaluationId, engine);
      } else {
        await evaluate(evaluationId, engine);
      }

      const evaluation = await pollEvaluationStatus(evaluationId, setProcessedSegments);
      const segmentsResponse = await getSegments(evaluationId);

      const updatedEvaluationData = {
        ...evaluation,
        segments: segmentsResponse.segments
      };
      setEvaluationData(updatedEvaluationData);

      if (segmentsResponse?.segments && [4, 6].includes(evaluation.status)) {
        sendGA4Event('evaluate_success');
        setCurrentView(RESULTS_PAGE);
      } else {
        sendGA4Event('evaluate_error');
        throw new Error('No segments received in response');
      }
    } catch (error) {
      sendGA4Event('evaluate_error');
      console.error('Failed to evaluate translation:', error);
      setError({
        isOpen: false,
        message: 'Failed to evaluate translation. Please try again.',
        progressDisplay: true
      });
    }
  };

  const handleModelChange = async (modelId) => {
    setSelectedModelId(modelId);
    await updateEvaluation(evaluationId, "modelId", modelId);
    getPrompt(modelId)
      .then((prompt) => {
        const text = typeof prompt === 'string' ? prompt : prompt?.evaluationPrompt ?? '';
        setPromptText(text);
      })
      .catch((err) => console.error('Error fetching prompt:', err));
  };

  const handleSourceLanguageChange = async (lang) => {
    if (lang === 'Detect language') {
      setSourceLanguage('Detect language');
      
      const currentSegment = segments.find(segment => segment.id === 1);
      if (currentSegment && currentSegment.source.trim().length >= 3) {
        try {
          const matchedLang = await detectLanguageFromText(currentSegment.source.trim());
          if (matchedLang) {
            setSourceLanguage(matchedLang.name);
            setIsSourceAutoDetected(true);
            if (evaluationId) {
              await updateEvaluation(evaluationId, 'sourceLanguage', matchedLang.name);
            }
          }
        } catch (error) {
          console.error('Language detection failed for source:', error);
        }
      }
    } else {
      setSourceLanguage(lang);
      setIsSourceAutoDetected(false);
      if (evaluationId) {
        await updateEvaluation(evaluationId, 'sourceLanguage', lang);
      }
    }
  };

  const handleTargetLanguageChange = async (lang) => {
    if (lang === 'Detect language') {
      setTargetLanguage('Detect language');
      
      const currentSegment = segments.find(segment => segment.id === 1);
      if (currentSegment && currentSegment.target.trim().length >= 3) {
        try {
          const matchedLang = await detectLanguageFromText(currentSegment.target.trim());
          if (matchedLang) {
            setTargetLanguage(matchedLang.name);
            setIsTargetAutoDetected(true);
            if (evaluationId) {
              await updateEvaluation(evaluationId, 'targetLanguage', matchedLang.name);
            }
          }
        } catch (error) {
          console.error('Language detection failed for target:', error);
        }
      }
    } else {
      setTargetLanguage(lang);
      setIsTargetAutoDetected(false);
      if (evaluationId) {
        await updateEvaluation(evaluationId, 'targetLanguage', lang);
      }
    }
  };

  const handleGuidelinesChange = useCallback((newGuidelines) => {
    setGuidelines(newGuidelines);
  }, []);

  // ID validation: if not valid, show NotFound
  if (!isValidID(evaluationId)) {
    return <NotFound />;
  }

  if (currentView === PROGRESS_PAGE) {
    return <ProgressPage
        evaluationData={evaluationData}
        sourceLanguage={sourceLanguage}
        targetLanguage={targetLanguage}
        estimates={estimates}
        hasError={error?.progressDisplay}
        handleEvaluate={handleEvaluate}
        setError={setError}
        processedSegments={processedSegments}
        limits={limits}
      />;
  }

  if (currentView === RESULTS_PAGE) {
    return <ResultsPage 
      evaluationData={evaluationData} 
      sourceLanguage={sourceLanguage}
      targetLanguage={targetLanguage}
      listModels={fullListModels}
      limits={limits}
    />;
  }

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <h1 className="text-3xl font-bold mb-2">
            Evaluate and fix <span className="text-green-500">✓</span> the quality of translation using top-tier LLMs
          </h1>
          
          <div className="flex flex-col md:flex-row gap-4 md:gap-6 w-full max-w-full">
            <div className="flex-1 w-full max-w-full">
              <SegmentsPanel
                segments={segments}
                sourceLanguage={sourceLanguage}
                targetLanguage={targetLanguage}
                onSegmentChange={handleSegmentChange}
                onRemoveSegment={handleRemoveSegment}
                onAddSegment={handleAddSegment}
                onFileUpload={handleFileUpload}
                evaluationId={evaluationId}
                onSourceLanguageChange={handleSourceLanguageChange}
                onTargetLanguageChange={handleTargetLanguageChange}
                isSourceAutoDetected={isSourceAutoDetected}
                isTargetAutoDetected={isTargetAutoDetected}
                setIsSourceAutoDetected={setIsSourceAutoDetected}
                setIsTargetAutoDetected={setIsTargetAutoDetected}
                onPaste={handlePaste}
              />
            </div>
            <div className="w-full md:w-80 mt-6 md:mt-0 max-w-full" style={{ paddingTop: '54px' }}>
              <div className="space-y-6 sticky top-8 z-30">
                <OptionsPanel
                  sourceLanguage={sourceLanguage}
                  targetLanguage={targetLanguage}
                  onEvaluate={handleEvaluate}
                  onModelChange={handleModelChange}
                  estimates={estimates}
                  segments={segments}
                  models={fullListModels}
                  selectedModelId={selectedModelId}
                  promptText={promptText}
                  evaluationId={evaluationId}
                  guidelines={guidelines}
                  onGuidelinesChange={handleGuidelinesChange}
                />
                <HumanReviewBanner />
              </div>
            </div>
          </div>
        </>
      )}
      <ErrorDialog
        isOpen={error.isOpen}
        onClose={() => setError({ isOpen: false, message: '', progressDisplay: false })}
        title="Error"
        description={error.message}
      />
    </>
  );
};

export default EvaluatePage;
